import type { TestInclusion } from './guided-template.dto';
import { z } from 'zod/v3';

export const QuestionnaireDimensionStateEntrySchema = z.object({
  state: z.enum(['yes', 'remediate', 'compensate', 'accept']),
  note: z.string().max(4000).optional(),
  alternativeScope: z.string().max(4000).optional(),
  alternativeRationale: z.string().max(4000).optional(),
  alternativeTestIds: z.array(z.string().min(1)).max(20).optional(),
});
export type QuestionnaireDimensionStateEntryDTO = z.infer<typeof QuestionnaireDimensionStateEntrySchema>;

/** A selection alone must never become a claim of an effective alternative. */
export function hasAlternativeControlDetails(entry: QuestionnaireDimensionStateEntryDTO | null | undefined): boolean {
  return entry?.state === 'compensate' && (entry.note?.trim().length ?? 0) >= 20 &&
    Boolean(entry.alternativeScope?.trim()) && (entry.alternativeRationale?.trim().length ?? 0) >= 20;
}

/** Work ownership and deadlines come from the canonical remediation Task. */
export interface PolicyQuestionDimensionFollowupDTO {
  findingId: string;
  status: string;
  taskId: string | null;
  taskCode: string | null;
  taskStatus?: string | null;
  ownerId: string | null;
  ownerName: string | null;
  dueDate: string | null;
  remediationPlan: string | null;
}

export interface QuestionnaireRemediationPlanDTO {
  remediationPlan?: string;
  ownerId?: string | null;
  dueDate?: string | null;
}

/** Policy adoption is separate from evidence of implementation. */
export type PolicyQuestionAnswerMode = 'requirement' | 'value' | 'fact' | 'elsewhere';

export const POLICY_EXCLUSION_OPTION_KEY = 'exclude_from_policy';

export interface PolicyQuestionScopeDTO {
  inclusion: TestInclusion | 'context';
  applicable: boolean;
  includedByDefault: boolean;
}

/** Persisted findings shown separately from generated policy commitments. */
export interface PolicyQuestionRemediationDTO {
  id: string;
  blueprintId: string;
  title: string;
  description: string | null;
  status: string;
  ownerName: string | null;
  dueDate: string | null;
  testId: string | null;
  testCode: string | null;
  taskId?: string | null;
}

/** A reviewable suggestion; it never assigns work or changes an attestation. */
export const QuestionnaireGapDraftSchema = z.object({
  gapDetails: z.string().max(4000).nullable(),
  remediationPlan: z.string().max(4000),
  questions: z.array(z.string()).max(5),
});
export type QuestionnaireGapDraftDTO = z.infer<typeof QuestionnaireGapDraftSchema>;
export type QuestionnaireGapDraftInputDTO = {
  programId: string;
  blueprintId: string;
  dimensionKey: string;
  gapDetails: string;
  remediationPlan: string;
};
